import { formatDate, CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Shipment } from './models/shipment';
import { SessionStorageService } from '../services/session-storage.service';
import { MenuItem } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import {ShipmentService} from "./shipment.service";
import {Page} from "../models/page.model";
import { ProgressBarModule } from 'primeng/progressbar';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { ChipModule } from 'primeng/chip';
import { TooltipModule } from 'primeng/tooltip';
import { PageShellComponent } from '../shared/page-layout/page-shell.component';
import { PageHeroComponent } from '../shared/page-layout/page-hero.component';
import { PageDataPanelComponent } from '../shared/page-layout/page-data-panel.component';
import { LocaleService } from '../i18n/locale.service';

@Component({
    selector: 'app-list-shipment',
    templateUrl: './shipment.component.html',
    styleUrls: ['./shipment.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        ProgressBarModule,
        BreadcrumbModule,
        TableModule,
        ButtonModule,
        TagModule,
        ChipModule,
        TooltipModule,
        PageShellComponent,
        PageHeroComponent,
        PageDataPanelComponent
    ]
})
export class ShipmentComponent implements OnInit {
	private readonly localeService = inject(LocaleService);

	shipmentList: Shipment[] = [];
	shipmentMap: Map<number, Shipment[]> = new Map<number, Shipment[]>();
	isLoading = true;
	totalRecords: number | undefined;
	currentPage: number = 0;
	breadcrumbItems: MenuItem[];
	homeBreadcrumb: MenuItem;

	constructor(private shipmentService: ShipmentService,
		private sessionStorageService: SessionStorageService,
		private router: Router) { }

	ngOnInit(): void {
		this.breadcrumbItems = [{ label: this.t('pages.shipmentDirectory.breadcrumb.shipments'), routerLink: '/shipment' }];
		this.homeBreadcrumb = { icon: 'pi pi-home', routerLink: '/' };
	}

	loadOrderList($event: TableLazyLoadEvent) {
		const first = $event.first ?? 0;
		const rows = $event.rows ?? 20;
		this.currentPage = Math.floor(first / rows) + 1;
		if (this.shipmentMap.has(this.currentPage))
			this.shipmentList = this.shipmentMap.get(this.currentPage) || [];
		else {
			this.isLoading = true;
			this.shipmentService.searchShipments({
                customerId: this.sessionStorageService.getCustomer()?.id?.toString() ?? ''
            }, {
                page: this.currentPage,
                size: rows
            }).subscribe((shipments: Page<Shipment>) => {
				this.shipmentMap.set(this.currentPage, shipments.content);
				this.shipmentList = this.shipmentMap.get(this.currentPage) || [];
                this.totalRecords = shipments.totalElements;
				this.isLoading = false;
			});
		}
	}

	showDate(longDate: number) {
		return formatDate(new Date(longDate), 'dd-MM-yyyy HH:mm', 'en-US');
	}

	viewDetailPage(shipment: Shipment): void {
		this.router.navigate(
			['/shipment/view', shipment.shipmentId]
		);
	}

	displayShipmentStatus(shipment: Shipment): string {
		return shipment.workflowState || shipment.shipmentStatus || this.t('pages.shipmentDirectory.status.open');
	}

	getSeverity(status?: string): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
		const normalizedStatus = (status ?? '').toUpperCase();
		if (normalizedStatus.includes('COMPLETE') || normalizedStatus.includes('APPROVED') || normalizedStatus.includes('CONFIRMED')) {
			return 'success';
		}
		if (normalizedStatus.includes('CANCEL') || normalizedStatus.includes('REJECT')) {
			return 'danger';
		}
		if (normalizedStatus.includes('PENDING') || normalizedStatus.includes('DRAFT')) {
			return 'warn';
		}
		return 'info';
	}

	getResultText(): string {
		this.localeService.locale();
		const total = this.totalRecords ?? this.shipmentList.length;
		return this.t('pages.shipmentDirectory.results.shipments', { count: total });
	}

	statusLabel(status?: string): string {
		this.localeService.locale();
		const normalizedStatus = (status ?? '').toUpperCase();
		if (!normalizedStatus) return this.t('pages.shipmentDirectory.status.open');
		return this.localeService.translateIfPresent(`pages.shipmentDirectory.statusMap.${normalizedStatus.toLowerCase()}`, status ?? normalizedStatus);
	}

	t(key: string, fallbackOrParams?: string | Record<string, unknown>, params?: Record<string, unknown>): string {
		this.localeService.locale();
		if (typeof fallbackOrParams === 'string') {
			return this.localeService.translateIfPresent(key, fallbackOrParams, params);
		}
		return this.localeService.translate(key, fallbackOrParams);
	}

}
